import React, { useState, useEffect } from 'react'
import CloseIcon from '@material-ui/icons/Close'
import FlipCameraAndroidIcon from '@material-ui/icons/FlipCameraAndroid'
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography
} from '@material-ui/core'
import {
  withOneplaceLibraryContext,
  IOneplaceLibraryContextProp
} from '../OneplaceLibraryProvider'
import Camera, { FACING_MODES } from 'react-html5-camera-photo'
import { processPhoto } from './utils/processPhoto'
import { convertBase64Image, getRandomFileName } from './utils/imageUtils'
import { IPhoto } from '../..'
import { logError } from '../../logging'

export interface IHtmlCameraProps extends IOneplaceLibraryContextProp {
  open: boolean
  onPhotoTaken: (photo: IPhoto) => void
  onCloseCamera: () => void
}

export const HtmlCamera = withOneplaceLibraryContext(
  (props: IHtmlCameraProps) => {
    const [cameraOpen, setCameraOpen] = useState<boolean>(false)
    const [idealFacingMode, setIdealFacingMode] = useState<string>(FACING_MODES.ENVIRONMENT)
    const [availableCameras, setAvailableCameras] = useState<number>(0)

    useEffect(() => {
      setCameraOpen(props.open)
    }, [props.open])

    const handleVideoDevicesCount = async (retryCount = 1) => {
      try {
        const devices = navigator.mediaDevices && await navigator.mediaDevices.enumerateDevices()
        const videoDevices = devices.filter(device => device.kind === 'videoinput')
        setAvailableCameras(videoDevices.length)
      } catch (err) {
        console.error('Error enumerating devices:', err)
        if (retryCount > 0) {
          //Retry once (not necessary)
          setTimeout(() => handleVideoDevicesCount(retryCount - 1), 1500)
        } else {
          setAvailableCameras(0)
        }
      }
    }

    useEffect(() => {
      if (cameraOpen) {
        handleVideoDevicesCount()
      }
    }, [cameraOpen])

    const handleTakePhoto = async (dataUri: string) => {
      try {
        const parts = dataUri.split(';base64,')
        const photo = await processPhoto(
          convertBase64Image(parts[1]).file,
          getRandomFileName() + '.png',
          props.ctx.imageStorage
        )
        photo.attachmentType = 'photo'
        photo.source = 'camera'
        props.onPhotoTaken(photo)
      } catch (e) {
        logError(e, 'Error while processing uploaded image')
      }
    }

    const handleSwitchCamera = () => {
      setIdealFacingMode(idealFacingMode === FACING_MODES.ENVIRONMENT ? FACING_MODES.USER : FACING_MODES.ENVIRONMENT)
    }

    return (
      <Dialog
        open={cameraOpen}
        aria-labelledby='form-dialog-title'
        fullWidth={false}
        maxWidth='md'
      >
        <AppBar>
          <Toolbar>
            <IconButton
              edge='start'
              color='inherit'
              onClick={() => {
                //setCameraOpen(false) parent component controls camera visibility
                props.onCloseCamera()
              }}
              aria-label='close'
            >
              <CloseIcon />
            </IconButton>
            <Typography variant='h6'>Camera</Typography>

            { availableCameras > 1 &&
              (<div style={{ marginLeft: 'auto'}}>
                <IconButton
                  aria-label="switch camera"
                  color="inherit"
                  title="Switch Camera"
                  onClick={handleSwitchCamera}
                >
                  <FlipCameraAndroidIcon />
                </IconButton>
              </div>)
            }
          </Toolbar>
        </AppBar>

        <Camera
          idealFacingMode={idealFacingMode}
          isFullscreen={false}
          onTakePhoto={handleTakePhoto}
          isImageMirror={false}
        />
      </Dialog>
    )
  }
)
